/** Variantes de `status` aceitas pelo componente `Tag` do ADS. */
export type ContractStatusTone = 'default' | 'highlight' | 'positive' | 'helper' | 'negative';

/**
 * Palavras-chave por tom, avaliadas em ordem. O backend devolve
 * `IndividualPaymentPersonCourseViewModel.status` como texto livre e localizado,
 * então o casamento é por substring normalizada em vez de um enum fechado.
 */
const TONE_KEYWORDS: readonly (readonly [ContractStatusTone, readonly string[]])[] = [
  ['negative', ['inadimplente', 'atrasad', 'vencid', 'cancelad', 'estornad', 'rejeitad']],
  ['positive', ['quitad', 'pago', 'paga', 'finalizad', 'conclu']],
  ['helper', ['aberto', 'aberta', 'pendente', 'aguardando', 'processand']],
  ['highlight', ['renegoci', 'isent', 'bolsa']],
];

/** Primeiro e último ponto de código do bloco Combining Diacritical Marks. */
const COMBINING_MARK_START = 0x0300;
const COMBINING_MARK_END = 0x036f;

/**
 * Remove acentos e caixa para tornar o casamento por substring estável.
 *
 * Exportada porque os predicados de estado da parcela
 * (`utils/parcelStatusPredicates.ts`) precisam da **mesma** normalização usada
 * aqui: se as duas divergissem, a Tag de status e o conjunto das ações em lote
 * poderiam discordar sobre a mesma parcela.
 *
 * @param value - Texto de status vindo do backend.
 * @returns Texto sem acentos e em minúsculas.
 *
 * @example
 * ```ts
 * normalizeStatusText('Cancelada'); // 'cancelada'
 * ```
 */
export function normalizeStatusText(value: string): string {
  return Array.from(value.normalize('NFD'))
    .filter(char => {
      const code = char.codePointAt(0) ?? 0;
      return code < COMBINING_MARK_START || code > COMBINING_MARK_END;
    })
    .join('')
    .toLowerCase();
}

/**
 * Mapeia o status textual de um contrato financeiro para a variante de `Tag`.
 *
 * Status desconhecido — ou ausente — degrada para `default` (Tag neutra) em vez
 * de quebrar a renderização, já que o backend pode introduzir textos novos.
 *
 * @param status - Texto de status vindo do backend.
 * @returns Variante de `Tag` correspondente.
 *
 * @example
 * ```ts
 * getContractStatusTone('Inadimplente'); // 'negative'
 * getContractStatusTone('Status novo');  // 'default'
 * ```
 */
export function getContractStatusTone(status: string | null | undefined): ContractStatusTone {
  if (!status) return 'default';
  const normalized = normalizeStatusText(status);
  for (const [tone, keywords] of TONE_KEYWORDS) {
    if (keywords.some(keyword => normalized.includes(keyword))) return tone;
  }
  return 'default';
}
